import type { FlowRanking, Item, ScenarioItem } from '../../core/domain/models'
import { resolveFlowPreferenceStatus } from '../../core/domain/flowPreferenceStatus'

export interface ComputeListFlowSectionsParams {
  items: Item[]
  rankings: Record<string, FlowRanking>
  scenarioItems?: ScenarioItem[]
}

export interface OrderedFlowItem {
  id: string
  name: string
  isGhost: boolean
}

export interface ListFlowSections {
  /** IDs of items in this list that are ordered in the flow */
  orderedIds: string[]
  /** IDs of items in this list that are NOT ordered in the flow */
  unorderedIds: string[]
  /** IDs of items marked as unavailable in the flow */
  unavailableIds: string[]
  /** IDs of items from other lists that are ordered in the flow (ghost items) */
  ghostIds: string[]
  /** Map of ghost item data by ID */
  ghostItemsById: Map<string, { id: string; name: string }>
  /** Combined ordered + ghost items sorted by rank (for rendering interspersed) */
  orderedWithGhosts: OrderedFlowItem[]
}

function compareNamesCaseInsensitive(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' })
}

export function computeListFlowSections(params: ComputeListFlowSectionsParams): ListFlowSections {
  const { items, rankings, scenarioItems = [] } = params

  const listItemIds = new Set(items.map(i => i.id))
  const scenarioItemsById = new Map(scenarioItems.map(si => [si.id, si]))

  const ordered: Array<{ id: string; name: string; r: number; isGhost: boolean }> = []
  const unavailable: Array<{ id: string; name: string; r: number }> = []
  const unordered: Array<{ id: string; name: string }> = []
  const ghost: Array<{ id: string; name: string; r: number }> = []
  const ghostItemsById = new Map<string, { id: string; name: string }>()

  // Process items that are in the list
  for (const item of items) {
    const ranking = rankings[item.id]
    if (ranking) {
      const status = resolveFlowPreferenceStatus(ranking)
      if (status === 'notAvailable') {
        unavailable.push({ id: item.id, name: item.name, r: ranking.r })
      } else {
        ordered.push({ id: item.id, name: item.name, r: ranking.r, isGhost: false })
      }
    } else {
      unordered.push({ id: item.id, name: item.name })
    }
  }

  // Process items from flow rankings that are NOT in the current list (ghost items)
  for (const [itemId, ranking] of Object.entries(rankings)) {
    if (listItemIds.has(itemId)) continue // Already processed above
    const status = resolveFlowPreferenceStatus(ranking)
    if (status === 'notAvailable') continue // Don't show unavailable ghost items
    
    const scenarioItem = scenarioItemsById.get(itemId) // Fallback for old flows
    const name = ranking.n || scenarioItem?.name

    if (name) {
      ghost.push({ id: itemId, name: name, r: ranking.r })
      ghostItemsById.set(itemId, { id: itemId, name: name })
      // Also add to ordered array for combined sorting
      ordered.push({ id: itemId, name: name, r: ranking.r, isGhost: true })
    }
  }

  // Sort combined ordered items (real + ghost) by rank
  ordered.sort((a, b) => {
    if (a.r !== b.r) return a.r - b.r
    return compareNamesCaseInsensitive(a.name, b.name)
  })

  unavailable.sort((a, b) => {
    if (a.r !== b.r) return a.r - b.r
    return compareNamesCaseInsensitive(a.name, b.name)
  })

  // Keep unordered items in their original insertion order (by `order` field)
  // so that newly added items appear at the bottom of the "Not in order yet" section.
  // Use a pre-built Map for O(1) lookup instead of O(n) .find() per comparison.
  const itemOrderById = new Map<string, number>(items.map(i => [i.id, i.order ?? 0]))
  unordered.sort((a, b) => {
    const orderA = itemOrderById.get(a.id) ?? 0
    const orderB = itemOrderById.get(b.id) ?? 0
    if (orderA !== orderB) return orderA - orderB
    return compareNamesCaseInsensitive(a.name, b.name)
  })

  ghost.sort((a, b) => {
    if (a.r !== b.r) return a.r - b.r
    return compareNamesCaseInsensitive(a.name, b.name)
  })

  return {
    orderedIds: ordered.filter(i => !i.isGhost).map(i => i.id),
    unorderedIds: unordered.map(i => i.id),
    unavailableIds: unavailable.map(i => i.id),
    ghostIds: ghost.map(i => i.id),
    ghostItemsById,
    orderedWithGhosts: ordered.map(i => ({ id: i.id, name: i.name, isGhost: i.isGhost })),
  }
}

export interface ComputeDropTargetGhostsParams {
  orderedWithGhosts: OrderedFlowItem[]
  rankings: Record<string, { r: number }>
  droppedItemId: string
  newOrderedRealIds: string[]
}

export interface DropTargetGhostsResult {
  ghostItems: OrderedFlowItem[]
  prevRank: number | null
  nextRank: number | null
  /** ID of the real list item immediately before the drop zone (null = top of list) */
  prevRealId: string | null
  /** ID of the real list item immediately after the drop zone (null = bottom of list) */
  nextRealId: string | null
}

export function computeDropTargetGhosts(params: ComputeDropTargetGhostsParams): DropTargetGhostsResult {
  const { orderedWithGhosts, rankings, droppedItemId, newOrderedRealIds } = params

  const droppedIndex = newOrderedRealIds.indexOf(droppedItemId)
  if (droppedIndex === -1) return { ghostItems: [], prevRank: null, nextRank: null, prevRealId: null, nextRealId: null }

  const prevRealId = droppedIndex > 0 ? newOrderedRealIds[droppedIndex - 1] : null
  const nextRealId = droppedIndex < newOrderedRealIds.length - 1 ? newOrderedRealIds[droppedIndex + 1] : null

  const prevRank = prevRealId ? (rankings[prevRealId]?.r ?? null) : null
  const nextRank = nextRealId ? (rankings[nextRealId]?.r ?? null) : null

  const ghostItems = orderedWithGhosts.filter(item => {
    if (!item.isGhost) return false
    const itemRank = rankings[item.id]?.r
    if (itemRank == null) return false

    const afterPrev = prevRank === null || itemRank > prevRank
    const beforeNext = nextRank === null || itemRank < nextRank

    return afterPrev && beforeNext
  })

  return { ghostItems, prevRank, nextRank, prevRealId, nextRealId }
}
